import React, { useState, useCallback } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Switch from "react-switch";
import { FaInfoCircle } from "react-icons/fa";
import { useDropzone } from "react-dropzone";
import { createItemMenu } from "../../../src/services/ApiMenuService";
import { toast } from 'react-toastify';

interface CreateMenuServiceModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onSubmit: (menuData: {
    id: number;
    IndexNo: number;
    serviceNameEn: string;
    serviceNameTh: string;
    serviceNameVn: string;
    iconImageBase64: string;
    bgColor: string;
    linkUrl: string;
    menuSites: { menuId: number; site: string; active: boolean }[];
    eventHandler: string;
    isActive: boolean;
  }) => void;
  currentIndex: number;
}

const CreateMenuServiceModal: React.FC<CreateMenuServiceModalProps> = ({
  isOpen,
  onRequestClose,
  onSubmit,
  currentIndex,
}) => {
  const [serviceNameEn, setServiceNameEn] = useState("");
  const [serviceNameTh, setServiceNameTh] = useState("");
  const [serviceNameVn, setServiceNameVn] = useState("");
  const [iconImageBase64, setIconImageBase64] = useState("");
  const [bgColor, setBgColor] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [eventHandler, setEventHandler] = useState("");
  const [menuSites, setMenuSites] = useState([
    { menuId: 1, site: "PPT", active: true },
    { menuId: 2, site: "PPV", active: false },
  ]);
  const [isActive, setIsActive] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [iconImageError, setIconImageError] = useState(false);
  const [linkUrlError, setLinkUrlError] = useState(false);

  const handleMenuSiteChange = (site: string, isChecked: boolean) => {
    const newMenuSites = menuSites.map((menuSite) =>
      menuSite.site === site ? { ...menuSite, active: isChecked } : menuSite
    );
    setMenuSites(newMenuSites);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setIconImageBase64(base64String.split(",")[1]);
      setPreviewImage(base64String);
      setIconImageError(false);
    };
    reader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/png": [".png"] },
    multiple: false,
  });

  const handleRemoveImage = () => {
    setIconImageBase64("");
    setPreviewImage(null);
    setIconImageError(true);
  };

  const handleBgColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (
      /^rgba?\((\d+,\s*\d+,\s*\d+,\s*[\d\.]+)\)$/.test(value) ||
      /^#[0-9A-F]{6}$/i.test(value)
    ) {
      setBgColor(value);
    }
  };

  const handleLinkUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLinkUrl(value);
    setLinkUrlError(!isValidUrl(value));
  };

  const isValidUrl = (url: string) => {
    const regex = /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm;
    return regex.test(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!iconImageBase64) {
      setIconImageError(true);
      return;
    }

    if (!isValidUrl(linkUrl)) {
      setLinkUrlError(true);
      return;
    }

    const menuData = {
      id: 0, // id should be generated by the backend
      IndexNo: currentIndex,
      serviceNameEn,
      serviceNameTh,
      serviceNameVn,
      iconImageBase64,
      bgColor,
      linkUrl,
      menuSites,
      eventHandler,
      isActive,
    };

    console.log("OBJ===>", menuData);
    try {
      await createItemMenu(menuData);
      onSubmit(menuData);

      // แสดงข้อความแจ้งเตือนเมื่อสร้างเมนูสำเร็จ
      toast.success('The new menu has been created successfully!');
    } catch (error) {
      console.error("Error saving data:", error);

      // แสดงข้อความแจ้งเตือนเมื่อเกิดข้อผิดพลาด
      toast.error('An error occurred while creating the menu. Please try again.');
    }

    setServiceNameEn("");
    setServiceNameTh("");
    setServiceNameVn("");
    setIconImageBase64("");
    setBgColor("");
    setLinkUrl("");
    setEventHandler("");
    setMenuSites([
      { menuId: 1, site: "PPT", active: true },
      { menuId: 2, site: "PPV", active: false },
    ]);
    setIsActive(false);
    setPreviewImage(null);
    onRequestClose();
  };

  return (
    <Modal show={isOpen} onHide={onRequestClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Menu</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group controlId="formServiceNameEn">
                <Form.Label>
                  Service Name (EN){" "}
                  <FaInfoCircle title="Enter the service name in English" />
                </Form.Label>
                <Form.Control
                  type="text"
                  value={serviceNameEn}
                  onChange={(e) => setServiceNameEn(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formServiceNameTh">
                <Form.Label>
                  Service Name (TH){" "}
                  <FaInfoCircle title="Enter the service name in Thai" />
                </Form.Label>
                <Form.Control
                  type="text"
                  value={serviceNameTh}
                  onChange={(e) => setServiceNameTh(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group controlId="formServiceNameVn">
                <Form.Label>
                  Service Name (VN){" "}
                  <FaInfoCircle title="Enter the service name in Vietnamese" />
                </Form.Label>
                <Form.Control
                  type="text"
                  value={serviceNameVn}
                  onChange={(e) => setServiceNameVn(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formBgColor">
                <Form.Label>
                  Background Color{" "}
                  <FaInfoCircle title="Select the background color or enter RGBA value" />
                </Form.Label>
                <Form.Control
                  type="text"
                  value={bgColor}
                  placeholder="rgba(255, 255, 255, 1)"
                  onChange={handleBgColorChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={12}>
              <Form.Group controlId="formLinkUrl">
                <Form.Label>
                  Link URL{" "}
                  <FaInfoCircle title="Enter the URL link for the service" />
                </Form.Label>
                <Form.Control
                  type="text"
                  value={linkUrl}
                  onChange={handleLinkUrlChange}
                  isInvalid={linkUrlError}
                  placeholder="set empty input `#`"
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid URL.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-2">
            {serviceNameEn !== "Payroll Calendar" && (
              <Col md={6}>
                <Form.Group controlId="formEventHandler">
                  <Form.Label>Low Code</Form.Label>
                  <div>
                    <Switch
                      onChange={(checked) => {
                        setEventHandler(checked ? "handleLowCode" : "");
                      }}
                      checked={eventHandler === "handleLowCode"}
                      aria-label="Event Handler switch"
                    />
                  </div>
                  {eventHandler === "handleLowCode" && (
                    <div style={{ fontSize: "14px", color: "red" }}>
                      "handleLowCode" = (&AccessToken='xxxx'&Language='th')
                    </div>
                  )}
                </Form.Group>
              </Col>
            )}
            <Col md={6}>
              <Form.Group controlId="formIsActive">
                <Form.Label>Status</Form.Label>
                <div>
                  <Switch
                    onChange={(checked) => setIsActive(checked)}
                    checked={isActive}
                    aria-label="Status switch"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group controlId="formIconImageBase64" className="mt-2">
            <Form.Label>
              Icon Image (PNG){" "}
              <FaInfoCircle title="Upload a PNG image for the icon" />
            </Form.Label>
            <div
              {...getRootProps()}
              className={`dropzone border p-4 text-center ${
                isDragActive ? "bg-light" : ""
              } ${iconImageError ? "border-danger" : ""}`}
              style={{ cursor: "pointer" }}
            >
              <input {...getInputProps()} />
              {previewImage ? (
                <div className="d-flex flex-column align-items-center">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="img-thumbnail mb-2"
                    style={{ width: "100px", height: "100px" }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleRemoveImage}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <><p>Drag & drop a PNG image here, or click to select file</p><p style={{color:'red'}}>recommend size (100px X 100px)</p></>
              )}
            </div>
            {iconImageError && (
              <div className="text-danger mt-2">Icon Image is required.</div>
            )}
          </Form.Group>
          <div className="d-flex justify-content-end mt-4">
            <Button
              variant="secondary"
              onClick={onRequestClose}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateMenuServiceModal;
